import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { getDuration, useReliableInterval } from '@common-fe/common-fe';
import _ from 'lodash';

import useContactResendVerificationStore from '@/modules/employer/components/SetupEmployer/ContactsSecurity/stores/useContactResendVerification.store';
import { getTimeRemaining } from '@/utils/modifiers';

import useSendVerificationCodeForContactQuery, { VerificationType } from '../queries/useSendVerificationCodeForContact.query';

enum VerificationServerError {
  EMAIL_ALREADY_VERIFIED = 'Email is already verified',
}

interface Props {
  valueKey: string;
  value: string;
  contactId: string;
  setVerificationErrorText?: (value: string) => void;
}

const useResendVerificationForContact = ({ valueKey, value, contactId, setVerificationErrorText }: Props) => {
  const { timestamps, resetTimestamp, setSendTimestamp } = useContactResendVerificationStore();
  const { sendVerificationCodeForContact, isSendCodeLoading } = useSendVerificationCodeForContactQuery();
  const [remainingTime, setRemainingTime] = useState(0);

  const handleResend = useCallback(async () => {
    try {
      await sendVerificationCodeForContact({
        type: VerificationType.EMAIL,
        is_link: true,
        contact_id: contactId,
        value: value,
      });
  
      setSendTimestamp(valueKey,  new Date().getTime());
    } catch (error) {
      const errorMessage = _.get(error, 'response.data.elevate_error_message', '') as string;
      if (errorMessage.includes(VerificationServerError.EMAIL_ALREADY_VERIFIED)) {
        setVerificationErrorText?.('This email address has been just verified. Refresh the page.');
      }
    }
  }, [sendVerificationCodeForContact, setSendTimestamp, setVerificationErrorText, valueKey, value, contactId]);

  const isDisabled = useMemo(() => isSendCodeLoading || remainingTime > 0, [isSendCodeLoading, remainingTime]);
  const defaultRemainingTime = useMemo(() => getTimeRemaining(timestamps?.[valueKey], getDuration({minutes: 1}), getDuration({ seconds: 1})),
    [timestamps, valueKey]);

  const updateRemainingTime = useCallback((time: number) => {
    const currentRemainingTime = time - 1;
    if (currentRemainingTime <= 0) {
      resetTimestamp(valueKey);
    }
    return currentRemainingTime;
  }, [valueKey, resetTimestamp]);

  const { start, clear } =  useReliableInterval(() => {
    if (defaultRemainingTime > 0) {
      setRemainingTime(updateRemainingTime);
    } 
  },getDuration({seconds: 1}));

  useEffect(() => {
    if (defaultRemainingTime > 0) {
      start();
  
      return () => {
        clear();
      };
    } else {
      resetTimestamp(valueKey);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultRemainingTime, updateRemainingTime]);

  useLayoutEffect(() => {
    setRemainingTime(defaultRemainingTime);
  }, [defaultRemainingTime]);

  return {
    handleResend,
    isDisabled,
    remainingTime,
  };
};

export default useResendVerificationForContact;
